/**
 * The small pieces a step's evidence is drawn with, shared by the run page and
 * the result views that sit beside it (GoldenResult in RunDetail, SceneResult
 * in its own file). Moved out of RunDetail.tsx unchanged on 2026-09-10 so a
 * result view can live in its own module without importing the page it is
 * rendered by.
 */
import type React from "react";
import i18n from "../i18n";

export function Mono({ children }: { children: React.ReactNode }) {
  return (
    <pre className="mt-0.5 overflow-x-auto whitespace-pre-wrap break-words rounded bg-slate-50 p-2 font-mono text-[11px] leading-relaxed dark:bg-slate-800">
      {children}
    </pre>
  );
}

export function Chip({ ok, children }: { ok?: boolean; children: React.ReactNode }) {
  const tone =
    ok === undefined
      ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
      : ok
        ? "bg-success-100 text-success-800 dark:bg-success-950 dark:text-success-300"
        : "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300";
  return (
    <span className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${tone}`}>
      {children}
    </span>
  );
}

/** A scalar as text; anything structured as indented JSON, which beats nothing. */
export function scalarOrJson(v: unknown, cap = 2000): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  const s = JSON.stringify(v, null, 1);
  return s.length > cap ? s.slice(0, cap) + "\n" + i18n.t("runDetail.truncated") : s;
}

/** Key/value rows for a flat object; JSON for anything deeper. */
export function Rows({ obj }: { obj: Record<string, unknown> }) {
  const flat = Object.entries(obj).every(
    ([, v]) => v === null || typeof v !== "object",
  );
  if (!flat) return <Mono>{scalarOrJson(obj)}</Mono>;
  return (
    <dl className="mt-0.5 grid grid-cols-[minmax(0,8rem)_1fr] gap-x-3 gap-y-0.5">
      {Object.entries(obj).map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="truncate font-mono text-[11px] text-slate-500">{k}</dt>
          <dd className="break-words font-mono text-[11px] text-slate-700 dark:text-slate-300">
            {String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}
